import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Download, Edit2, Eye, Briefcase, ChevronLeft, ChevronRight } from 'react-feather';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import config from '../../../../config';
import CustomizerContext from '../../../../_helper/Customizer';
import '../../sadmin.css';

const PAGE_SIZE = 12;

const formatDate = (s) => {
    if (!s) return '';
    const d = new Date(s);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
};

const ConcessionaireTable = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { layoutURL } = useContext(CustomizerContext);
    const layout = layoutURL || 'default';

    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };

    const [concessionaires, setConcessionaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);
    const [sortKey, setSortKey] = useState('concessionaire_name');
    const [sortDir, setSortDir] = useState('asc');

    useEffect(() => {
        // Permission gate — matches V2 behavior.
        if (localStorage.getItem('usertype') !== 'sadmin') {
            alert(t('You are not authorized to view this page'));
            window.history.back();
            return;
        }
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${config.hostname}/concessionaire`, configheader)
            .then((response) => {
                if (cancelled) return;
                setConcessionaires(Array.isArray(response.data) ? response.data : []);
                setLoading(false);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setErrorMsg(t('Could not load concessionaires.'));
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const base = q
            ? concessionaires.filter((c) =>
                  [c.concessionaire_name, c.concessionaire_code, c.concessionaire_email, c.concessionaire_phone]
                      .filter(Boolean)
                      .some((v) => String(v).toLowerCase().includes(q)),
              )
            : concessionaires.slice();
        base.sort((a, b) => {
            const av = (a[sortKey] || '').toString().toLowerCase();
            const bv = (b[sortKey] || '').toString().toLowerCase();
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return base;
    }, [concessionaires, query, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    useEffect(() => {
        if (page >= totalPages) setPage(0);
    }, [page, totalPages]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const handleExport = () => {
        const exportData = concessionaires.map((c) => ({
            'Concessionaire Name': c.concessionaire_name,
            'Concessionaire Code': c.concessionaire_code,
            Email: c.concessionaire_email,
            'Phone Number': c.concessionaire_phone,
            Address: c.concessionaire_address,
            'License Start Date': formatDate(c.concessionaire_license_start_date),
            'License End Date': formatDate(c.concessionaire_license_end_date),
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Concessionaires');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Concessionaires.xlsx');
    };

    const handleAdd = () =>
        navigate(`${process.env.PUBLIC_URL}/controlpanel/concessionaire/addnewconcessionaire/${layout}`);

    const handleView = (row) =>
        navigate(`${process.env.PUBLIC_URL}/controlpanel/concessionaire/viewconcessionairedetail/${row._id}`);

    const handleEdit = (row) =>
        navigate(`${process.env.PUBLIC_URL}/controlpanel/concessionaire/editconcessionaire/${row._id}`);

    const sortArrow = (key) => (sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : '');

    return (
        <div className="v3 v3-sadmin">
            <header className="v3-sadmin-hero">
                <div className="v3-sadmin-hero-text">
                    <div className="v3-label">{t('Concessionaires')}</div>
                    <h1 className="v3-display v3-sadmin-greeting" style={{ fontSize: 26 }}>
                        {t('All concessionaires')}
                    </h1>
                    <p className="v3-body v3-sadmin-sub">
                        {t('Manage every mall on the platform. Click a row to view details, or add a new one.')}
                    </p>
                </div>
                <div className="v3-sadmin-hero-actions">
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={handleExport}>
                        <Download size={14} />
                        {t('Export Excel')}
                    </button>
                    <button type="button" className="v3-btn v3-btn-primary" onClick={handleAdd}>
                        <Plus size={14} />
                        {t('New concessionaire')}
                    </button>
                </div>
            </header>

            <section className="v3-card v3-sadmin-list-card">
                <div className="v3-sadmin-list-header">
                    <div className="v3-sadmin-list-title">
                        <h2 className="v3-heading">{t('List')}</h2>
                        <span className="v3-sadmin-list-count">{filtered.length}</span>
                    </div>
                    <div className="v3-sadmin-list-actions">
                        <div className="v3-sadmin-search">
                            <Search size={14} aria-hidden />
                            <input
                                type="search"
                                placeholder={t('Search name, code, email, phone')}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setPage(0);
                                }}
                                aria-label={t('Search concessionaires')}
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="v3-sadmin-empty">
                        <p>{t('Loading concessionaires…')}</p>
                    </div>
                ) : errorMsg ? (
                    <div className="v3-sadmin-empty">
                        <h3>{t("Couldn't load")}</h3>
                        <p>{errorMsg}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="v3-sadmin-empty">
                        <Briefcase size={28} aria-hidden />
                        <h3>{concessionaires.length === 0 ? t('No concessionaires yet') : t('No matches')}</h3>
                        <p>
                            {concessionaires.length === 0
                                ? t('Create the first one with "New concessionaire" above.')
                                : t('Try a different search term.')}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="v3-sadmin-table-wrap">
                            <table className="v3-sadmin-table">
                                <thead>
                                    <tr>
                                        <th
                                            onClick={() => handleSort('concessionaire_name')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {t('Name')} {sortArrow('concessionaire_name')}
                                        </th>
                                        <th
                                            onClick={() => handleSort('concessionaire_code')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {t('Code')} {sortArrow('concessionaire_code')}
                                        </th>
                                        <th
                                            onClick={() => handleSort('concessionaire_email')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {t('Email')} {sortArrow('concessionaire_email')}
                                        </th>
                                        <th>{t('Phone')}</th>
                                        <th style={{ textAlign: 'right' }}>{t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageData.map((c) => (
                                        <tr key={c._id} onClick={() => handleView(c)}>
                                            <td className="v3-sadmin-cell-strong">{c.concessionaire_name}</td>
                                            <td>
                                                <span className="v3-sadmin-cell-code">{c.concessionaire_code}</span>
                                            </td>
                                            <td className="v3-sadmin-cell-muted">{c.concessionaire_email}</td>
                                            <td className="v3-sadmin-cell-muted">{c.concessionaire_phone}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <div className="v3-sadmin-row-actions">
                                                    <button
                                                        type="button"
                                                        className="v3-sadmin-icon-btn"
                                                        title={t('View')}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleView(c);
                                                        }}
                                                    >
                                                        <Eye size={13} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="v3-sadmin-icon-btn"
                                                        title={t('Edit')}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(c);
                                                        }}
                                                    >
                                                        <Edit2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="v3-sadmin-pagination">
                            <span>
                                {t('Showing')} <strong>{page * PAGE_SIZE + 1}</strong>–
                                <strong>{Math.min((page + 1) * PAGE_SIZE, filtered.length)}</strong> {t('of')}{' '}
                                <strong>{filtered.length}</strong>
                            </span>
                            <div className="v3-sadmin-pagination-controls">
                                <button
                                    type="button"
                                    className="v3-sadmin-page-btn"
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                    aria-label={t('Previous page')}
                                >
                                    <ChevronLeft size={13} />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i)
                                    .slice(
                                        Math.max(0, Math.min(page - 2, totalPages - 5)),
                                        Math.max(5, Math.min(page + 3, totalPages)),
                                    )
                                    .map((i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className={`v3-sadmin-page-btn ${i === page ? 'active' : ''}`}
                                            onClick={() => setPage(i)}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                <button
                                    type="button"
                                    className="v3-sadmin-page-btn"
                                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                    disabled={page >= totalPages - 1}
                                    aria-label={t('Next page')}
                                >
                                    <ChevronRight size={13} />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default ConcessionaireTable;
